import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function PlusSession() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [q, setQ] = useState(null);
  const [pos, setPos] = useState(1);
  const [total, setTotal] = useState(10);
  const [selected, setSelected] = useState(null);
  const [done, setDone] = useState(false);
  const [results, setResults] = useState(null);
  const presentedAt = useRef(Date.now());

  const load = useCallback(async () => {
    setSelected(null);
    try {
      const { data } = await api.get(`/premium/sessions/${sid}/next`);
      if (data.done) {
        const r = await api.get(`/premium/sessions/${sid}/results`);
        setResults(r.data); setDone(true); return;
      }
      setQ(data.question); setPos(data.position); setTotal(data.total);
      presentedAt.current = Date.now();
    } catch (e) {
      toast.error("Could not load question");
    }
  }, [sid]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (selected == null) return toast.error("Pick an option");
    try {
      await api.post(`/premium/sessions/${sid}/answer`, {
        assessment_id: sid, question_id: q.question_id,
        selected_index: selected,
        response_time_ms: Date.now() - presentedAt.current,
      });
      await load();
    } catch { toast.error("Save failed"); }
  };

  if (done && results) {
    return (
      <div><Nav />
        <div className="max-w-2xl mx-auto px-5 py-10">
          <button onClick={()=>nav("/plus")} className="mb-3 text-sm text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Plus
          </button>
          <div className="uppercase font-mono text-xs tracking-[0.24em] text-[#E0A96D] mb-2">Module complete</div>
          <h1 className="font-serif text-4xl mb-3" data-testid="plus-results-title">{results.module.title}</h1>
          <p className="text-slate-400 text-sm italic mb-6">{results.disclaimer}</p>

          <div className="space-y-4" data-testid="plus-patterns">
            {Object.entries(results.pattern_counts).map(([domain, tags]) => (
              <div key={domain} className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-5">
                <div className="uppercase font-mono text-xs tracking-widest text-slate-500 mb-2">{domain.replace(/_/g, " ")}</div>
                <div className="space-y-1">
                  {Object.entries(tags).map(([tag, n]) => (
                    <div key={tag} className="flex items-center gap-3">
                      <div className="flex-1 text-sm capitalize text-slate-200">{tag.replace(/_/g, " ")}</div>
                      <div className="flex-1 h-2 rounded-full bg-slate-800 overflow-hidden">
                        <div className="h-full rounded-full bg-[#E0A96D]" style={{width: `${(n/Math.max(1, Object.values(tags).reduce((a,b)=>a+b,0)))*100}%`}} />
                      </div>
                      <div className="w-10 text-right font-mono text-xs text-slate-300">{n}</div>
                    </div>
                  ))}
                </div>
                {results.dominant_patterns[domain] && (
                  <div className="mt-2 text-xs text-slate-500 font-mono">Dominant · {results.dominant_patterns[domain]}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!q) return <div><Nav /><div className="max-w-xl mx-auto px-5 py-20 text-slate-400">Loading…</div></div>;

  return (
    <div><Nav />
      <div className="max-w-2xl mx-auto px-5 py-8">
        <div className="flex items-center justify-between text-xs font-mono uppercase tracking-widest mb-3">
          <span className="text-[#E0A96D]">DualQ Plus · {q.domain.replace(/_/g, " ")}</span>
          <span className="text-slate-500">{pos} / {total}</span>
        </div>
        <div className="rounded-2xl bg-[#131E36] border border-slate-800/70 p-6 sm:p-8 fade-up" data-testid="plus-question-card">
          <h2 className="font-serif text-2xl sm:text-3xl leading-snug mb-6" data-testid="plus-question-prompt">{q.prompt}</h2>
          <div className="space-y-2">
            {q.options.map((opt, i) => (
              <button key={i} onClick={()=>setSelected(i)}
                className={`w-full text-left px-4 py-3.5 rounded-xl border transition-colors ${selected===i?"border-[#E0A96D] bg-[#E0A96D]/10 text-white":"border-slate-700 hover:border-slate-500 text-slate-200"}`}
                data-testid={`plus-option-${i}`}>
                <span className="font-mono text-xs text-slate-500 mr-3">{String.fromCharCode(65+i)}</span>{opt}
              </button>
            ))}
          </div>
          <button onClick={submit}
            className="mt-6 w-full py-3.5 rounded-xl bg-[#E0A96D] text-[#070C18] font-semibold hover:bg-[#F0C28B]"
            data-testid="plus-submit">Continue</button>
        </div>
      </div>
    </div>
  );
}
